import { useEffect, useState, useMemo } from 'react';
import { Accordion, Table, Group, Text, Badge, Button, Paper, Container, TextInput, Select, Alert } from '@mantine/core';
import { IconSearch, IconAlertCircle } from '@tabler/icons-react';
import { useSearchParams } from 'react-router-dom';
import api from '../services/api';
import { useTranslation } from '../context/TranslationContext';
import { useAuth } from '../context/AuthContext';
import FreeItemBadge from '../components/FreeItemBadge';
import RefundItemBadge from '../components/RefundItemBadge';
import { formatPrice } from '../utils/formatPrice';
import { formatWeight } from '../utils/formatWeight';
import { parsePickupWindow, formatPickupDate } from '../utils/pickupDates';
import { localizedField } from '../utils/localizedField';
import { buildBreakdownRows, creditAmountFromItems, splitByCredit } from '../utils/orderBreakdown';
import { getShippingDominantRate } from '../utils/shippingVat';

export default function MyOrders() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeOrder, setActiveOrder] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [paymentFilter, setPaymentFilter] = useState('');
  const [statusMessage, setStatusMessage] = useState(null);
  const [statusType, setStatusType] = useState('');
  const urlOrderId = searchParams.get('orderId');

  const showStatus = (message, type) => {
    setStatusMessage(message);
    setStatusType(type);
    setTimeout(() => {
      setStatusMessage(null);
      setStatusType('');
    }, 5000);
  };

  const downloadInvoice = async (orderId) => {
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001/api'}/orders/${orderId}/invoice`, {
        credentials: 'include'
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({ error: t('invoiceDownloadFailed', 'Failed to download invoice.') }));
        showStatus(data.error || t('invoiceDownloadFailed', 'Failed to download invoice.'), 'error');
        return;
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 15000);
    } catch (err) {
      console.error('Invoice download error:', err);
      showStatus(err.message || t('invoiceDownloadFailed', 'Failed to download invoice.'), 'error');
    }
  };

  useEffect(() => {
    const fetchOrders = async () => {
      try {
        const data = await api.getUserOrders();
        const ordersWithRefunds = await Promise.all(
          data.map(async (order) => {
            try {
              const refunds = await api.getOrderRefunds(order.id);
              return { ...order, refunds };
            } catch {
              return { ...order, refunds: [] };
            }
          })
        );
        setOrders(ordersWithRefunds);
        if (urlOrderId && data.some(o => o.id === urlOrderId)) {
          setActiveOrder(urlOrderId);
        }
      } catch (err) {
        console.error('Failed to fetch orders', err);
      } finally {
        setLoading(false);
      }
    };
    fetchOrders();
  }, [urlOrderId]);

  const filteredOrders = useMemo(() =>
    orders.filter(o => {
      if (statusFilter && o.status !== statusFilter) return false;
      if (paymentFilter && o.payment_status !== paymentFilter) return false;
      if (searchQuery) {
        const q = searchQuery.toLowerCase();
        const matchId = o.id.toLowerCase().includes(q);
        const matchItem = o.items?.some(item =>
          (localizedField(item, 'name', t.langCode) || item.name || item.product_sku)?.toLowerCase().includes(q)
        );
        if (!matchId && !matchItem) return false;
      }
      return true;
    }),
    [orders, statusFilter, paymentFilter, searchQuery, t.langCode]
  );

  const groupedOrders = useMemo(() => {
    const groups = {};
    filteredOrders.forEach(o => {
      const d = new Date(o.created_at);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      if (!groups[key]) groups[key] = [];
      groups[key].push(o);
    });
    return groups;
  }, [filteredOrders]);

  const sortedGroupKeys = useMemo(() =>
    Object.keys(groupedOrders).sort().reverse(),
    [groupedOrders]
  );

  const monthLocale = t.langCode === 'de' ? 'de-CH' : 'en-US';

  if (loading) return <Container py="xl">{t('loading', 'Loading...')}</Container>;

  return (
    <div className="dashboard-orders-view dashboard-view-wrapper" style={{ padding: '0' }}>
      <header className="dashboard-page-header">
        <div>
          <h1 className="dashboard-page-title">{t('myOrders', 'Orders')}</h1>
          <p className="dashboard-page-description">{t('myOrdersDesc')}</p>
        </div>
      </header>

      {user?.account_balance != null && Number(user.account_balance) !== 0 && (
        <Paper withBorder p="md" mb="md" style={{ background: '#f0fdf4', borderColor: '#bbf7d0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <Text fw={600}>{t('accountBalance', 'Account Balance')}</Text>
            <Text size="sm" c="dimmed">{t('availableCredit', 'Available Credit')}</Text>
          </div>
          <Text fw={700} size="lg" c="green">{formatPrice(user.account_balance)}</Text>
        </Paper>
      )}

      {statusMessage && (
        <Alert
          icon={<IconAlertCircle size={16} />}
          color={statusType === 'error' ? 'red' : 'green'}
          mb="md"
          role={statusType === 'error' ? 'alert' : 'status'}
          aria-live={statusType === 'error' ? 'assertive' : 'polite'}
        >
          {statusMessage}
        </Alert>
      )}

      <Paper withBorder p="sm" mb="md" role="group" aria-label={t('orderFilters', 'Order filters')}>
        <Group gap="sm">
          <TextInput
            placeholder={t('searchOrders', 'Search orders…')}
            leftSection={<IconSearch size={16} aria-hidden="true" />}
            value={searchQuery}
            onChange={e => setSearchQuery(e.currentTarget.value)}
            style={{ flex: 1 }}
            aria-label={t('searchOrders', 'Search orders by ID or product')}
          />
          <Select
            placeholder={t('allStatuses', 'All statuses')}
            data={[
              { value: 'pending', label: t('pending', 'Pending') },
              { value: 'confirmed', label: t('confirmed', 'Confirmed') },
              { value: 'partially_shipped', label: t('partially_shipped', 'Partially Shipped') },
              { value: 'shipped', label: t('shipped', 'Shipped') },
              { value: 'cancelled', label: t('cancelled', 'Cancelled') },
              { value: 'refunded', label: t('refunded', 'Refunded') },
            ]}
            value={statusFilter}
            onChange={setStatusFilter}
            clearable
            w={180}
            aria-label={t('filterByStatus', 'Filter by order status')}
          />
          <Select
            placeholder={t('allPayments', 'All payments')}
            data={[
              { value: 'paid', label: t('paid', 'Paid') },
              { value: 'unpaid', label: t('unpaid', 'Unpaid') },
              { value: 'overdue', label: t('overdue', 'Overdue') },
            ]}
            value={paymentFilter}
            onChange={setPaymentFilter}
            clearable
            w={180}
            aria-label={t('filterByPayment', 'Filter by payment status')}
          />
        </Group>
      </Paper>

      {filteredOrders.length === 0 ? (
        <Text>{orders.length === 0 ? t('noOrdersYet', 'You have no orders yet.') : t('noMatchingOrders', 'No orders match your filters.')}</Text>
      ) : (
        sortedGroupKeys.map(groupKey => {
          const [year, month] = groupKey.split('-');
          const monthName = new Date(year, month - 1).toLocaleDateString(monthLocale, { year: 'numeric', month: 'long' });

          return (
            <div key={groupKey} style={{ marginBottom: '2rem' }}>
              <Text fw={700} size="lg" mb="sm">{monthName}</Text>
              <Accordion variant="separated" value={activeOrder} onChange={setActiveOrder}>
                {groupedOrders[groupKey].map(o => {
            const vatExclusiveOrder = user?.user_type === 'business';
            const hasFulfillments = o.fulfillments && o.fulfillments.length > 0;
            // Inline delivery-note button only shows for a single issued fulfillment that has its PDF
            const inlineNoteButton = o.fulfillments?.length === 1 && !!o.fulfillments[0].delivery_note_file_id;

            return (
              <Accordion.Item key={o.id} value={o.id}>
                <Accordion.Control>
                  <Group position="apart" spacing="md">
                    {/* Left Side: Order Information */}
                    <Group spacing="xl" style={{ flexGrow: 1 }}>
                      <Text fw={500} style={{ minWidth: 110 }}>{t('orderNumber', 'Order')} #{o.id.substring(0, 8)}</Text>
                      <Text size="sm" c="dimmed" style={{ minWidth: 90 }}>{new Date(o.created_at).toLocaleDateString()}</Text>
                      <Text fw={600} style={{ minWidth: 100 }}>{formatPrice(o.total_price)}</Text>
                      <Badge color={o.refunds?.length > 0 && o.status !== 'refunded' ? 'yellow' : undefined}>
                        {o.refunds?.length > 0 && o.status !== 'refunded' ? t('partiallyRefunded', 'Partially Refunded') : t(o.status, o.status)}
                      </Badge>
                      <Badge color={o.payment_status === 'paid' ? 'green' : 'orange'}>
                        {o.payment_method === 'monthly' ? (t('monthlyInvoice', 'Monthly Invoice')) : (t(o.payment_status, o.payment_status))}
                      </Badge>
                    </Group>

                    {/* Right Side: Download Buttons */}
                    <Group spacing="sm">
                      <Button 
                        variant="outline"
                        size="xs"
                        style={inlineNoteButton || o.fulfillments?.length > 1 ? {} : { marginRight: '1.5rem' }}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation(); 
                          downloadInvoice(o.id);
                        }}
                      >
                        {t('downloadInvoice', 'Download Invoice')}
                      </Button>

                      {/* If there is exactly 1 fulfillment with a delivery note, show it here inline to be helpful */}
                      {inlineNoteButton && (
                        <Button 
                          variant="light"
                          color="indigo"
                          size="xs"
                          style={{ marginRight: '1.5rem' }}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation(); 
                            window.open(`${process.env.REACT_APP_API_URL || 'http://localhost:3001/api'}/orders/fulfillments/${o.fulfillments[0].id}/delivery-note`, '_blank');
                          }}
                        >
                          {t('downloadDeliveryNote', 'Delivery Note')}
                        </Button>
                      )}
                      
                      {o.fulfillments?.length > 1 && (
                        <Badge style={{ marginRight: '1.5rem' }} color="indigo" variant="light">
                          {o.fulfillments.length} {t('deliveries', 'Deliveries')}
                        </Badge>
                      )}
                    </Group>
                  </Group>
                </Accordion.Control>
                
                <Accordion.Panel>
                  <Paper p="sm" radius="md">
                    {o.pickup_location_id && (o.pickup_date || o.pickup_time) && (
                      <Text size="sm" mb="sm">
                        <strong>{t('pickupSlot', 'Pickup')}:</strong>{' '}
                        {[
                          o.pickup_date ? formatPickupDate(o.pickup_date, t.langCode) : null,
                          (() => {
                            const w = parsePickupWindow(o.pickup_time);
                            return w ? `${w.start}–${w.end}` : null;
                          })(),
                        ].filter(Boolean).join(', ')}
                      </Text>
                    )}
                    <Text fw={600} mb="sm">{t('orderItems', 'Order Items')}</Text>
                    <Table striped highlightOnHover mb="md">
                      <caption className="sr-only">{t('orderItemsCaption', 'Order items table with product name, quantity, discount and price')}</caption>
                      <thead>
                        <tr>
                          <th scope="col" style={{ textAlign: 'left', width: '58%' }}>{t('product', 'Product')}</th>
                          <th scope="col" style={{ textAlign: 'center', width: '14%' }}>{t('qty', 'Qty')}</th>
                          <th scope="col" style={{ textAlign: 'center', width: '14%' }}>{t('takeOrderDiscountPct', 'Discount %')}</th>
                          <th scope="col" style={{ textAlign: 'right', width: '14%' }}>{t('price', 'Price')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {o.items?.map(item => {
                          const isFree = Number(item.unit_price_final) === 0;
                          // A negative quantity is a money-back line, not goods: it is
                          // never shipped and never stocked.
                          const isCredit = Number(item.quantity) < 0;
                          const discountPercent = Number(item.discount_percent) || 0;
                          const discountTxt = (discountPercent > 0 && !isFree) ? `${discountPercent}%` : '';
                          // Retail shows the SHOWN per-unit gross (centime-rounded)
                          // × qty — the same money the invoice renders (net 4.82 @
                          // 2.6% → shown 4.95 → 6 pcs → 29.70, never 29.67).
                          const shownUnitFinal = vatExclusiveOrder
                            ? Number(item.unit_price_final)
                            : (Number(item.vat_rate) > 0
                              ? Math.round(Number(item.unit_price_final) * (1 + Number(item.vat_rate) / 100) * 100) / 100
                              : Number(item.unit_price_final));
                          return (
                          <tr key={item.id} style={isFree ? { background: '#f0fdf4' } : {}}>
                            <th scope="row" style={{ textAlign: 'left', verticalAlign: 'middle' }}>
                              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                                <span>{localizedField(item, 'name', t.langCode) || item.name || item.product_sku}</span>
                                {item.weight_value != null && (
                                  <span style={{ color: '#888', fontSize: '0.85em' }}>
                                    {formatWeight(item.weight_value, item.weight_unit)}
                                  </span>
                                )}
                                {isFree && <FreeItemBadge />}
                                {isCredit && <RefundItemBadge />}
                              </span>
                              {item.substitution_note && (
                                <span style={{ display: 'block', color: '#12b886', fontSize: '0.85em', marginTop: 2 }}>
                                  {t('substitutionNote', '', { product: localizedField(item, 'name', t.langCode) || item.name || item.product_sku || '' })}
                                </span>
                              )}
                            </th>
                            <td style={{ textAlign: 'center' }}>
                              {item.quantity}
                              {!isCredit && Number(item.total_shipped) > 0 ? <Text size="xs" color="dimmed">({formatWeight(item.total_shipped, item.weight_unit)} {t('shipped', 'Shipped')})</Text> : null}
                            </td>
                            <td style={{ textAlign: 'center' }}>{discountTxt}</td>
                            <td style={{ textAlign: 'right', verticalAlign: 'middle', ...(isCredit ? { color: '#c2410c', fontWeight: 600 } : {}) }}>
                              {formatPrice(shownUnitFinal * item.quantity)}
                              {vatExclusiveOrder ? (
                                <Text size="xs" c="dimmed">
                                  {t('exclVAT', 'excl. VAT')}
                                </Text>
                              ) : (item.vat_rate ? (
                                <Text size="xs" c="dimmed">
                                  {`${t('incl', 'incl.')} ${item.vat_rate}% ${t('vat', 'VAT')}`}
                                </Text>
                              ) : null)}
                            </td>
                          </tr>
                          );
                        })}
                      </tbody>
                      <tfoot style={{ borderTop: '2px solid #eee' }}>
                        {o.appliedDiscounts && (() => {
                          const d = o.appliedDiscounts;
                          const appliedBalance = Number(d.applied_balance || 0);
                          const isPickup = !!o.pickup_location_id;
                          const persistedDiff = d.round_off_diff != null ? Number(d.round_off_diff) : null;
                          const derivedFromTb = d.total_before_rounding != null
                            ? Number(o.total_price || 0) - Number(d.total_before_rounding || 0)
                            : null;
                          const rawDiff = persistedDiff ?? derivedFromTb ?? 0;
                          const roundingDiff = Math.round(rawDiff * 100) / 100;

                          // Business (net) display uses the snapshot's NET amounts
                          // as-is. Retail always displays GROSS, so gross-up each NET
                          // amount at the order's dominant VAT rate. shipping_cost
                          // (legacy fallback) is already GROSS, so it is used as-is.
                          // Refund lines are money back, not goods: they must not
                          // drag the dominant rate the rest of the order is shown at.
                          const { goods } = splitByCredit(o.items || []);
                          const dominantRate = getShippingDominantRate(goods.map(i => ({
                            net: (Number(i.unit_price_final) || 0) * (Number(i.quantity) || 1),
                            vat_rate: Number(i.vat_rate) || 0,
                          })));
                          const gf = 1 + dominantRate / 100;
                          const gross = (net) => Math.round((Number(net) || 0) * gf * 100) / 100;

                          const shippingNet = d.net_shipping != null ? Number(d.net_shipping) : null;

                          const coupons = vatExclusiveOrder
                            ? (d.coupons || [])
                            : (d.coupons || []).map(c => ({ ...c, computed_discount: gross(c.computed_discount) }));

                          // The cart discount resolved on the merchandise net minus the
                          // earlier discounts, so its base comes from the same ledger.
                          const ledgerNet = Array.isArray(d.discount_rows)
                            ? d.discount_rows.reduce((sum, r) => sum + Number(r.amount_net || 0), 0)
                            : Number(d.rule_discount || 0) + Number(d.volume_discount || 0);
                          const cartBase = Math.max(0, Number(d.net_subtotal || 0) - ledgerNet);

                          const rows = buildBreakdownRows({
                            t,
                            lang: t.langCode,
                            couponDiscounts: coupons,
                            // A running order's ledger supersedes the legacy rule/volume pair and is
                            // itemised row by row. Retail shows GROSS, so the NET ledger
                            // amounts are grossed up at the same dominant rate as every other
                            // line here.
                            discountRows: Array.isArray(d.discount_rows)
                              ? (vatExclusiveOrder
                                  ? d.discount_rows
                                  : d.discount_rows.map(r => ({ ...r, amount_net: gross(r.amount_net) })))
                              : null,
                            cartDiscountPercent: Number(d.cart_discount_percent || 0),
                            cartDiscountBase: vatExclusiveOrder ? cartBase : gross(cartBase),
                            // Refund lines are NET on the order, so they follow the same
                            // viewer basis as every other line here.
                            creditAmount: creditAmountFromItems(o.items || [], { vatExclusive: vatExclusiveOrder }),
                            ruleDiscount: vatExclusiveOrder ? Number(d.rule_discount || 0) : gross(d.rule_discount),
                            shippingCost: vatExclusiveOrder
                              ? (shippingNet ?? Number(d.shipping_cost || 0))
                              : (shippingNet != null ? gross(shippingNet) : Number(d.shipping_cost || 0)),
                            shippingFree: !!d.shipping_free,
                            isPickup,
                            volumeDiscount: vatExclusiveOrder ? Number(d.volume_discount || 0) : gross(d.volume_discount),
                            volumeDiscountPercentage: d.volume_discount_percentage != null ? Number(d.volume_discount_percentage) : null,
                            appliedBalance,
                            roundOffDiff: roundingDiff,
                            vatExclusive: vatExclusiveOrder,
                            netSubtotal: vatExclusiveOrder ? Number(d.net_subtotal || 0) : 0,
                            netShipping: 0,
                            vatOnDiscount: 0,
                            vatAmount: Number(o.vat_amount || 0),
                          });
                          return rows.map((row) => {
                            const isBalanceDue = row.kind === 'balance' && row.sign > 0;
                            const style = row.kind === 'discount' || row.free
                              ? { color: '#16a34a', fontSize: '0.9em' }
                              : row.kind === 'refund'
                                ? { color: '#c2410c', fontSize: '0.9em' }
                                : isBalanceDue
                                ? { color: '#c2410c', fontSize: '0.9em' }
                                : row.kind === 'rounding'
                                  ? { color: '#666', fontSize: '0.9em' }
                                  : row.kind === 'subtotal'
                                    ? { fontWeight: 600 }
                                    : undefined;
                            const value = row.free
                              ? t('free', 'FREE')
                              : `${row.sign < 0 ? '-' : (row.kind === 'subtotal' || row.kind === 'vat') ? '' : row.sign > 0 ? '+' : ''}${formatPrice(row.amount)}`;
                            return (
                              <tr key={row.key}>
                                <td colSpan="3" style={{ textAlign: 'right', ...style }}>{row.label}</td>
                                <td style={{ textAlign: 'right', ...style }}>{value}</td>
                              </tr>
                            );
                          });
                        })()}
                        <tr>
                          <td colSpan="3" style={{ textAlign: 'right', fontWeight: 600 }}>{t('total', 'Total')}:</td>
                          <td style={{ textAlign: 'right', fontWeight: 600 }}>{formatPrice(o.total_price)}</td>
                        </tr>
                        {!vatExclusiveOrder && Number(o.vat_amount) > 0 ? (
                            <tr>
                              <td colSpan="3" style={{ textAlign: 'right', color: '#666', fontSize: '0.85em' }}>* {t('vat', 'VAT')}:</td>
                              <td style={{ textAlign: 'right', color: '#666', fontSize: '0.85em' }}>{formatPrice(o.vat_amount)}</td>
                            </tr>
                        ) : null}
                      </tfoot>
                    </Table>

                    {/* Show Fulfillments inside if there are multiple, or just show universally */}
                    {hasFulfillments && (
                      <div style={{ marginTop: '1.5rem' }}>
                        <Text fw={600} mb="sm">{t('deliveries', 'Deliveries')}</Text>
                        <Group spacing="md">
                          {o.fulfillments.map((f, i) => (
                            <Paper key={f.id} withBorder p="sm" style={{ width: '100%' }}>
                              <Group position="apart" style={{ width: '100%' }}>
                                <div style={{ flexGrow: 1 }}>
                                  <Text size="sm" fw={500}>
                                    {f.delivery_note_file_id
                                      ? `${t('deliveryNote', 'Delivery Note')} #${f.id.substring(0, 8)}`
                                      : `${t('delivery', 'Delivery')} #${f.id.substring(0, 8)}`}
                                  </Text>
                                  <Text size="xs" c="dimmed">
                                    {t('shipped', 'Shipped')}: {f.fulfilled_at ? new Date(f.fulfilled_at).toLocaleDateString() : new Date(f.created_at).toLocaleDateString()}
                                    {f.delivered_at ? ` · ${t('delivered', 'Delivered')}: ${new Date(f.delivered_at).toLocaleDateString()}` : ''}
                                  </Text>
                                </div>
                                {f.delivery_note_file_id && (
                                  <Button 
                                    variant="light"
                                    color="indigo"
                                    size="xs"
                                    onClick={() => window.open(`${process.env.REACT_APP_API_URL || 'http://localhost:3001/api'}/orders/fulfillments/${f.id}/delivery-note`, '_blank')}
                                  >
                                    {t('downloadNote', 'Download Note')}
                                  </Button>
                                )}
                              </Group>
                            </Paper>
                          ))}
                        </Group>
                      </div>
                    )}

                    {/* Show Refunds */}
                    {o.refunds && o.refunds.length > 0 && (
                      <div style={{ marginTop: '1.5rem' }}>
                        <Text fw={600} mb="sm">{t('refundHistory', 'Refund History')}</Text>
                        {o.refunds.map((r) => (
                          <Paper key={r.id} withBorder p="sm" mb="sm" style={{ width: '100%' }}>
                            <Group position="apart">
                              <div>
                                <Text size="sm" fw={500}>{t('creditNote', 'Credit Note')} #{r.id.substring(0,8)}</Text>
                                <Text size="xs" c="dimmed">{t('date', 'Date')}: {new Date(r.created_at).toLocaleDateString()}</Text>
                                <Text size="xs" c="dimmed">{t('amount', 'Amount')}: {formatPrice(r.total_refund_amount)}</Text>
                                <Badge color={r.status === 'processed' ? 'green' : 'red'} size="sm">
                                  {t(r.status, r.status)}
                                </Badge>
                              </div>
                              <Button 
                                variant="light"
                                color="teal"
                                size="xs"
                                onClick={() => window.open(`${process.env.REACT_APP_API_URL || 'http://localhost:3001/api'}/orders/refunds/${r.id}/credit-note`, '_blank')}
                              >
                                {t('downloadCreditNote', 'Download Credit Note')}
                              </Button>
                            </Group>
                          </Paper>
                        ))}
                      </div>
                    )}
                  </Paper>
                </Accordion.Panel>
              </Accordion.Item>
            );
          })}
              </Accordion>
            </div>
          );
        })
      )}
    </div>
  );
}